import { useState, useEffect, useCallback } from 'react';
import api from '@/libs/api';
import { AccountDto } from '@/libs/api/account';
import { useEventBus } from '@/libs/hooks/use-event-bus';

// Event name for account follow status changes
const ACCOUNT_FOLLOW_EVENT = 'account-follow-status-change';

// Event payload type
interface AccountFollowEvent {
  accountName: string;
  isFollowing: boolean;
}

/**
 * Hook for handling account follow functionality
 */
export const useAccountFollow = (account?: AccountDto | null, initialFollowing?: boolean) => {
  const [isFollowing, setIsFollowing] = useState<boolean>(initialFollowing || false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [followTimeout, setFollowTimeout] = useState<NodeJS.Timeout>();

  // Memoize the handler function to prevent unnecessary re-renders
  const handleFollowEvent = useCallback((data: AccountFollowEvent) => {
    // Only edit if this is for the same account
    if (account?.name && data.accountName === account.name) {
      setIsFollowing(data.isFollowing);
    }
  }, [account?.name]);

  // Initialize event bus for follow status changes
  const { emit } = useEventBus<AccountFollowEvent>(ACCOUNT_FOLLOW_EVENT, handleFollowEvent);

  useEffect(() => {
    setIsFollowing(initialFollowing || false);
  }, [initialFollowing]);

  // Check if the current user is following the account
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!account?.name) return;

      try {
        setIsLoading(true);
      } catch (error) {
        console.error('Error checking follow status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkFollowStatus().then();
  }, [account?.name]);

  const handleFollow = useCallback((shouldFollow: boolean) => {
    if (!account?.name) return;

    if (followTimeout) {
      clearTimeout(followTimeout);
    }

    setFollowTimeout(setTimeout(async () => {
      try {
        setIsLoading(true);
        if (shouldFollow) {
          await api.account.follow(account.name);
        } else {
          await api.account.unFollow(account.name);
        }
        setIsFollowing(shouldFollow);

        // Emit event to notify other components about the follow status change
        emit({
          accountName: account.name,
          isFollowing: shouldFollow
        });
      } catch (error) {
        console.error('Error toggling follow:', error);
      } finally {
        setIsLoading(false);
        setFollowTimeout(undefined);
      }
    }, 300));
  }, [account?.name, followTimeout, emit]);

  return {
    isFollowing,
    isLoading,
    handleFollow,
  };
};
